import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import { Box, Tooltip } from '@mui/material';
import HoveredStructureLabel from '~/viewportGrid/tile/HoveredStructureLabel';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import {
    MouseEvent as ReactMouseEvent,
    PointerEvent as ReactPointerEvent,
    ReactElement,
    ReactNode,
    useEffect,
    useRef,
    useState,
} from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { useDoseDisplaySettings } from '~/hooks/dose/useDoseDisplaySettings';
import { useElementSize } from '~/hooks/useElementSize';
import {
    FrameAxis,
    useFrameRotationReadout,
} from '~/hooks/viewer/useFrameRotationReadout';
import { currentStructurePeekState } from '~/state/atoms/structureSet/roiAtoms';
import {
    showSensitiveInfoState,
    tileSliceDetailsShownState,
} from '~/state/atoms/viewerAtoms';
import {
    READOUT,
    READOUT_BOX_SX,
    READOUT_DIM,
    READOUT_FONT,
    TILE_CORNER_HEIGHT_VAR,
    WARNING,
} from '../chrome';
import {
    CursorReadout,
    DrawnImage,
    ImageDetailsContext,
    RenderableVolume,
    RenderAdapter,
} from '../adapters/types';
import { useChangeVersion } from '../adapters/useChangeVersion';
import { ImageLayer, Plane } from '../layout/types';
import {
    edgeLabelsForPlane,
    formatCursorDensity,
    formatCursorDose,
    formatCursorValue,
    formatPatientCoordsLine,
    formatPlanePositionLine,
    obliqueReasonLines,
    FRAME_AXIS_OF_PLANE,
    modalityBeside,
    paneAccessibleName,
    RoiSelectionHandler,
    ScaleBar,
    scaleBarFor,
    distinguishingSeriesNumber,
    ListedImage,
    sharesNameWithAnotherSeries,
    ShownImage,
    TileStructureActions,
} from './tileViewModel';
import AiGeneratedMark from './AiGeneratedMark';
import EdgeLetters from './EdgeLetters';
import ImageWarningsMark from './ImageWarningsMark';
import FrameAngleReadout from './FrameAngleReadout';
import FrameResetButton from './FrameResetButton';
import { tileFrameSx } from './tileFrame';
import type { PlaneCycleControl } from '~/viewportGrid/layout/planeCycle';
import type { RowFrameControls } from './tileLink';
import TileCorner from './TileCorner';
import TileTitleBar, {
    RotatePlanesButton,
    TITLE_BAR_DELIMITER_SX,
} from './TileTitleBar';
import SliceDetailsButton from './SliceDetailsButton';
import { useSliceIndicator } from './useSliceIndicator';
import { useHoveredRoi } from './useHoveredRoi';
import { WindowHistogramImage } from './WindowHistogram';

/**
 * One image a tile title names: its modality, unless its name begins with
 * it, then its name, masked with the privacy toggle; its series number when
 * the name alone does not tell it apart from `listedImages` (see
 * {@link distinguishingSeriesNumber}). An element, not a component, so a
 * tooltip the host hangs on it can attach to it.
 */
function titleImageName(
    intl: IntlShape,
    image: ShownImage,
    listedImages: ReadonlyArray<ListedImage>,
    testIdPrefix: string,
    namesShown: boolean,
    fullNameOnHover: boolean
): ReactElement {
    const seriesNumberLabel = distinguishingSeriesNumber(
        intl,
        image.seriesNumber,
        namesShown,
        sharesNameWithAnotherSeries(image, listedImages)
    );
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                minWidth: 0,
                overflow: 'hidden',
            }}
        >
            {modalityBeside(image.modality, image.label) ? (
                <Box
                    component="span"
                    data-testid={`${testIdPrefix}-modality`}
                    sx={{ color: READOUT_DIM }}
                >
                    {image.modality}
                </Box>
            ) : null}
            <Box
                component="span"
                data-testid={`${testIdPrefix}-image-name`}
                title={fullNameOnHover ? image.label : undefined}
                sx={{
                    color: READOUT,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                <SensitiveInfoTypography>{image.label}</SensitiveInfoTypography>
            </Box>
            {seriesNumberLabel !== undefined ? (
                <Box
                    component="span"
                    data-testid={`${testIdPrefix}-series-number`}
                    sx={{ color: READOUT_DIM }}
                >
                    {seriesNumberLabel}
                </Box>
            ) : null}
        </Box>
    );
}

/**
 * The slice figures of the title bar. The caution marks always show: a
 * plane that crosses the image's slices names no slice, and reads OBLIQUE
 * in the caution colour with how far the plane lies off the slices, and
 * why in its tooltip; a slice that was interpolated rather than acquired
 * reads INTERPOLATED. A tile the row's turn takes off the slices keeps its
 * way back. The slice number out of the slice count and, after a divider, the
 * image's slice spacing show only while the user keeps the slice details
 * in the title bar. Only the number is bright: it is the figure that moves
 * as the user scrolls, while the count and the spacing hold still.
 */
function SliceIndicatorReadout({
    renderer,
    frame,
    axis,
}: {
    renderer: RenderAdapter;
    frame: RowFrameControls;
    /** The patient axis whose turn the tile's own angle readout shows. */
    axis: FrameAxis;
}) {
    const intl = useIntl();
    const detailsShown = useRecoilValue(tileSliceDetailsShownState);
    const indicator = useSliceIndicator(renderer, frame.source);
    const frameReadout = useFrameRotationReadout(
        frame.source.getFrameRotation()
    );
    if (!indicator) {
        return null;
    }
    // A tile the turn takes off the slices keeps a way back even where its
    // own angle reads 0.0 and so carries no reset.
    const offersReset =
        indicator.kind === 'oblique' &&
        frameReadout !== undefined &&
        frameReadout.angles[axis] === undefined;
    const interpolated = indicator.kind === 'slice' && indicator.interpolated;
    if (!detailsShown && indicator.kind === 'slice' && !interpolated) {
        return null;
    }
    return (
        <Box
            component="span"
            data-testid="slice-indicator"
            sx={{
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: '8px',
                flexShrink: 0,
                fontFamily: READOUT_FONT,
                fontSize: 11,
            }}
        >
            {detailsShown && indicator.kind === 'slice' ? (
                <Box component="span" sx={{ color: READOUT }}>
                    {indicator.index}
                    <Box component="span" sx={{ color: READOUT_DIM }}>
                        {` / ${indicator.count}`}
                    </Box>
                </Box>
            ) : null}
            {interpolated ? (
                <Tooltip
                    title={intl.formatMessage({
                        id: 'slice.interpolated.tooltip',
                    })}
                    placement="bottom"
                >
                    <Box
                        component="span"
                        // Focusable, so the tooltip opens from the
                        // keyboard as well.
                        tabIndex={0}
                        data-testid="slice-interpolated"
                        sx={{ color: WARNING, fontWeight: 700 }}
                    >
                        {intl.formatMessage({ id: 'slice.interpolated.badge' })}
                    </Box>
                </Tooltip>
            ) : null}
            {indicator.kind === 'oblique' ? (
                <Tooltip
                    title={
                        <Box
                            data-testid="slice-oblique-details"
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                            }}
                        >
                            {obliqueReasonLines(
                                intl,
                                indicator.angle,
                                indicator.cause
                            ).map((line) => (
                                <Box component="span" key={line}>
                                    {line}
                                </Box>
                            ))}
                        </Box>
                    }
                    placement="bottom"
                >
                    <Box
                        component="span"
                        // Focusable, so the tooltip opens from the
                        // keyboard as well.
                        tabIndex={0}
                        data-testid="slice-oblique-reason"
                        sx={{
                            display: 'inline-flex',
                            gap: '4px',
                            color: WARNING,
                            fontWeight: 700,
                        }}
                    >
                        <Box component="span" data-testid="slice-oblique">
                            {intl.formatMessage({ id: 'obliqueBadge' })}
                        </Box>
                        <Box component="span" data-testid="slice-oblique-angle">
                            {intl.formatMessage(
                                { id: 'slice.oblique.angle' },
                                { angle: indicator.angle }
                            )}
                        </Box>
                    </Box>
                </Tooltip>
            ) : null}
            {offersReset ? (
                <FrameResetButton
                    label={frameReadout.snapLabel}
                    onReset={frame.onReset}
                />
            ) : null}
            {detailsShown ? (
                <>
                    <Box
                        sx={{ ...TITLE_BAR_DELIMITER_SX, alignSelf: 'center' }}
                    />
                    <Box
                        component="span"
                        data-testid="slice-sampling"
                        sx={{ color: READOUT_DIM }}
                    >
                        {indicator.sampling.sliceSpacing}
                    </Box>
                </>
            ) : null}
        </Box>
    );
}

/**
 * Where the tile's plane lies: its signed offset along its normal in cm,
 * named by the patient axis it is read along, in the frame of the image
 * the tile shows, or nothing before the renderer mounts. The offset is the
 * plane's own, so it holds while the shared point moves within an oblique
 * plane.
 */
function PlanePositionReadout({ renderer }: { renderer: RenderAdapter }) {
    useChangeVersion(renderer.getCursorSource());
    const position = renderer.getPlanePosition();
    if (position === undefined) {
        return null;
    }
    return (
        <Box data-testid="plane-position" sx={READOUT_BOX_SX}>
            {formatPlanePositionLine(position)}
        </Box>
    );
}

/**
 * The four orientation edge letters (R/L/A/P/H/F). The letters are derived from
 * the mounted view's real orientation, so a flipped or oblique view relabels
 * R/L — patient laterality must never mislabel. Subscribing to the renderer's
 * cursor source (which notifies on mount) lets the real letters replace the
 * pre-mount fallback once the view is up.
 */
function EdgeChips({
    renderer,
    plane,
}: {
    renderer: RenderAdapter;
    plane: Plane;
}) {
    useChangeVersion(renderer.getCursorSource());
    const edges = edgeLabelsForPlane(plane, renderer.getEdgeLabels());
    return <EdgeLetters {...edges} />;
}

const NO_IMAGES: ReadonlyArray<DrawnImage> = [];
const NO_LISTED_IMAGES: ReadonlyArray<ListedImage> = [];
const NO_HISTOGRAMS: ReadonlyArray<WindowHistogramImage> = [];

interface TileProps {
    volume: RenderableVolume;
    plane: Plane;
    /** Which of the row's fused pair this tile draws. */
    layer?: ImageLayer;
    /**
     * The image the tile draws, for its labels: the row's dataset unless
     * the tile draws the fused secondary alone.
     */
    shownImage?: ShownImage;
    /**
     * The fused image a blended pane also draws, named in the title after
     * the row's image; absent on any other pane.
     */
    fusedImage?: ShownImage;
    /**
     * Whether this pane names the image it draws in its title bar. See
     * {@link paneNamesItsImage} for the rule the grid applies.
     */
    namesItsImage?: boolean;
    /**
     * The images the grid offers. A title adds the series number to a name
     * that another of them shares.
     */
    listedImages?: ReadonlyArray<ListedImage>;
    renderer: RenderAdapter;
    /**
     * The oblique frame of the tile's row and the way back from it. The
     * tile shows the frame's turn about the axis its plane lies along.
     */
    frame: RowFrameControls;
    isActive?: boolean;
    isMaximized?: boolean;
    /**
     * Selects the ROI under a click, present only while a click can select:
     * `focus` is true for a double-click (select + centre the tiles on the ROI).
     */
    onSelectRoi?: RoiSelectionHandler;
    /**
     * What the hover label offers on the structure under the pointer; absent
     * on a pane whose structures are not the ones being edited.
     */
    structureActions?: TileStructureActions;
    /** Host-supplied overlay over the canvas (the engine overlays of a tile). */
    overlay?: ReactNode;
    /**
     * Host-supplied status of the images the tile draws, in its title bar
     * after the slice readout.
     */
    status?: ReactNode;
    /**
     * Host-supplied details of the image the tile names, hung on its name in
     * the title bar; the name alone when absent.
     */
    imageDetails?: (context: ImageDetailsContext) => ReactNode;
    /**
     * The images the tile draws; a generated one is marked first in the
     * title bar.
     */
    drawnImages?: ReadonlyArray<DrawnImage>;
    onMaximize?: () => void;
    /**
     * The row's plane control, on the pane that offers it; absent elsewhere,
     * and then no control appears.
     */
    planeCycle?: PlaneCycleControl;
    /** Whether the scale bar is drawn over the image. */
    scaleBarVisible?: boolean;
    /**
     * The images whose window histograms the tile draws in its corner while
     * the histogram toggle is on: each image it draws.
     */
    windowHistograms?: ReadonlyArray<WindowHistogramImage>;
    /** Pointer listeners for the title bar (the rearrange handle). */
    dragHandleProps?: Record<string, unknown>;
}

/**
 * One self-describing viewport tile. Pointer/wheel interaction is NOT handled
 * here — tile canvases are engine views, and their events flow through the
 * host-attached ViewManager -> ToolManager pipeline (the production tools).
 * The tile renders the HUD: the slice index and the plane position read
 * from the renderer (engine truth, via its CursorSource), and on the active
 * tile the value under the pointer with the point it was read at, and the
 * dose the tile draws there. The window/level values are read per series in
 * the row footer; the tile draws, in its corner, the histogram each window
 * sits in. Readout safety rules
 * (units, never HU on a non-CT modality, never Gy on a relative dose) live
 * in the view-model.
 */
function Tile({
    volume,
    plane,
    layer = ImageLayer.Blended,
    shownImage = volume.metadata,
    fusedImage,
    namesItsImage = true,
    listedImages = NO_LISTED_IMAGES,
    renderer,
    frame,
    isActive = false,
    isMaximized = false,
    onSelectRoi,
    structureActions,
    overlay,
    status,
    imageDetails,
    drawnImages = NO_IMAGES,
    onMaximize,
    planeCycle,
    scaleBarVisible = false,
    windowHistograms = NO_HISTOGRAMS,
    dragHandleProps,
}: TileProps) {
    const intl = useIntl();
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
    const [cursor, setCursor] = useState<CursorReadout | undefined>(undefined);
    // The title bar, which the details of the named image hang from.
    const [titleBar, setTitleBar] = useState<HTMLDivElement | null>(null);
    // The client point of the pointer over the view, so the readout under
    // it is read again when the view moves beneath a still pointer.
    const pointerRef = useRef<{ clientX: number; clientY: number }>();
    const [scaleBar, setScaleBar] = useState<ScaleBar | undefined>(undefined);
    const hoveredRoi = useHoveredRoi(renderer);
    const dropHoveredPick = hoveredRoi.dropPick;
    const viewportRef = useRef<HTMLDivElement>(null);
    // The corner stack's height is published on the view, so a legend in
    // the same corner sits above whatever the stack holds.
    const cornerRef = useRef<HTMLDivElement>(null);
    const cornerHeightPx = useElementSize(cornerRef)?.heightPx ?? 0;

    useEffect(() => {
        const canvas = canvasRef.current;
        const overlayCanvas = overlayCanvasRef.current;
        if (!canvas || !overlayCanvas) {
            return undefined;
        }
        renderer.mount({ canvas, overlayCanvas, volume, plane });
        return () => renderer.dispose();
    }, [renderer, volume, plane]);

    // Separate from the mount, so a tile can change sides of the fusion
    // without remounting its view.
    useEffect(() => {
        renderer.setImageLayer(layer);
    }, [renderer, layer]);

    // While shown, the scale bar states the view's real display scale (mm
    // per CSS pixel) at the canvas width, read when it is turned on and again
    // on every zoom, fit and resize the renderer announces. A hidden bar is
    // not measured and its last measure is dropped, so turning it on never
    // draws a scale from before. An unchanged bar keeps its state, so the
    // tile does not re-render on an announcement that changes nothing it
    // draws.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!scaleBarVisible) {
            setScaleBar(undefined);
            return undefined;
        }
        if (!canvas) {
            return undefined;
        }
        const update = () => {
            const next = scaleBarFor(
                renderer.getMmPerCssPixel(),
                canvas.getBoundingClientRect().width
            );
            setScaleBar((current) =>
                current?.lengthPx === next?.lengthPx &&
                current?.label === next?.label
                    ? current
                    : next
            );
        };
        update();
        return renderer.getDisplayScaleSource().subscribe(update);
    }, [renderer, scaleBarVisible]);

    // A resize moves the image under the hovered pick's client point, so it
    // drops the pick.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(dropHoveredPick);
        observer.observe(canvas);
        return () => observer.disconnect();
    }, [dropHoveredPick]);

    // A peek starting or ending changes which contours the view draws under
    // the pick, so it drops the pick.
    const peeking = useRecoilValue(currentStructurePeekState) !== null;
    useEffect(dropHoveredPick, [peeking, dropHoveredPick]);

    // A scroll, a pan, a crosshair move, a zoom or a change of the dose on
    // display changes what is read under a still pointer, so the pointer
    // readout is read again on every cursor and display-scale change.
    useEffect(() => {
        const readUnderPointer = () => {
            const pointer = pointerRef.current;
            if (pointer) {
                setCursor(
                    renderer.readCursor(pointer.clientX, pointer.clientY)
                );
            }
        };
        const unsubscribeCursor = renderer
            .getCursorSource()
            .subscribe(readUnderPointer);
        const unsubscribeScale = renderer
            .getDisplayScaleSource()
            .subscribe(readUnderPointer);
        return () => {
            unsubscribeCursor();
            unsubscribeScale();
        };
    }, [renderer]);

    // Display-only hover readouts (pointer value and position + ROI name)
    // for a pointer anywhere over the view, the host overlays over the
    // canvas included; all tool gestures flow through the engine pipeline
    // attached by the host, not through these React handlers. A move over
    // an element the view portals elsewhere, such as a tooltip, lies
    // outside the view and reads nothing.
    const followPointer = (event: ReactPointerEvent<HTMLElement>) => {
        if (!event.currentTarget.contains(event.target as Node)) {
            return;
        }
        pointerRef.current = { clientX: event.clientX, clientY: event.clientY };
        setCursor(renderer.readCursor(event.clientX, event.clientY));
        hoveredRoi.onPointerMove(event);
    };
    // A press anywhere in the view, over a host overlay included, starts a
    // gesture that hides the contour name until its release. A press on the
    // label leaves it up, so its button can act on the pick it shows.
    const holdPointerPress = (event: ReactPointerEvent<HTMLElement>) => {
        const target = event.target as Node;
        if (
            !event.currentTarget.contains(target) ||
            hoveredRoi.labelRef.current?.contains(target)
        ) {
            return;
        }
        hoveredRoi.onPointerDown();
    };
    const clearPointerReadouts = () => {
        pointerRef.current = undefined;
        setCursor(undefined);
        hoveredRoi.onPointerLeave();
        dropHoveredPick();
    };
    const pointerReadout = isActive ? cursor : undefined;
    // The display scale of the dose under the pointer, from the store the
    // dose controls write, so the line reads at the scale they show.
    const pointerDoseScale = useDoseDisplaySettings(
        pointerReadout?.dose?.doseId ?? null
    ).settings.doseScale;
    const pointerDose = pointerReadout?.dose
        ? formatCursorDose(pointerReadout.dose, pointerDoseScale)
        : undefined;
    const pointerDoseLine = pointerDose ? (
        <Box
            data-testid="pointer-dose"
            sx={{ ...READOUT_BOX_SX, color: '#fff' }}
        >
            {intl.formatMessage({ id: 'pointerDose' }, { value: pointerDose })}
        </Box>
    ) : null;
    const pointerDensity =
        pointerReadout?.value && pointerReadout.density
            ? formatCursorDensity(intl, pointerReadout.density)
            : undefined;
    const pick = hoveredRoi.pick;

    // Click-to-select the ROI under the cursor. The click that ends a pan
    // drag is suppressed, so a drag never selects. Every single click
    // consumes the suppression, also while no select can run, so it never
    // outlives the release click of its own drag; the ROI lookup runs only
    // while a select can. A double-click consumes and selects only while a
    // select can run, and also focuses.
    const selectRoiAt = (event: ReactMouseEvent, focus: boolean) => {
        if (renderer.consumeClickSuppression() || !onSelectRoi) {
            return;
        }
        const roi = renderer.readRoiAt(event.clientX, event.clientY);
        if (roi) {
            onSelectRoi(roi.roiId, focus);
        }
    };

    // The image this pane draws, named where the pane is, and on a blended
    // pane the fused image after it. The description is PHI (masked with the
    // privacy toggle); the modality code and the series number are DICOM
    // codes, shown in the clear. The host may hang each image's details on
    // its name.
    // The full name on hover while patient data is shown, since the bar cuts
    // a long one short, unless the host hangs the image's details there.
    const fullNameOnHover = showSensitiveInfo && !imageDetails;
    const withDetails = (
        seriesInstanceUid: SeriesInstanceUid,
        name: ReactElement,
        fusedOver?: SeriesInstanceUid
    ): ReactNode =>
        imageDetails?.({ seriesInstanceUid, fusedOver, name, titleBar }) ??
        name;
    const titleImages = (
        <Box
            component="span"
            data-testid="tile-image"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                minWidth: 0,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                fontFamily: READOUT_FONT,
                fontSize: 11,
            }}
        >
            {withDetails(
                shownImage.seriesInstanceUid,
                titleImageName(
                    intl,
                    shownImage,
                    listedImages,
                    'tile',
                    showSensitiveInfo,
                    fullNameOnHover
                )
            )}
            {fusedImage ? (
                <>
                    <Box component="span" sx={{ color: READOUT_DIM }}>
                        +
                    </Box>
                    {withDetails(
                        fusedImage.seriesInstanceUid,
                        titleImageName(
                            intl,
                            fusedImage,
                            listedImages,
                            'tile-fused',
                            showSensitiveInfo,
                            fullNameOnHover
                        ),
                        shownImage.seriesInstanceUid
                    )}
                </>
            ) : null}
        </Box>
    );

    return (
        <Box
            data-testid="viewport-tile"
            role="group"
            aria-label={paneAccessibleName(intl, shownImage, plane)}
            sx={tileFrameSx(isActive)}
        >
            <TileTitleBar
                barRef={setTitleBar}
                isMaximized={isMaximized}
                onMaximize={onMaximize}
                dragHandleProps={dragHandleProps}
                controls={
                    planeCycle ? <RotatePlanesButton {...planeCycle} /> : null
                }
            >
                <AiGeneratedMark images={drawnImages} />
                <ImageWarningsMark images={drawnImages} />
                {namesItsImage ? titleImages : null}
                <Box data-title-spacer sx={{ flex: 1 }} />
                <SliceIndicatorReadout
                    renderer={renderer}
                    frame={frame}
                    axis={FRAME_AXIS_OF_PLANE[plane]}
                />
                {status}
                {/* The plane sits last of the readouts, against the rotate
                    control that changes it, and parts from the slice figures
                    it does not belong to. */}
                <Box
                    sx={{
                        ...TITLE_BAR_DELIMITER_SX,
                        // Nothing to part from when no readout precedes it.
                        '[data-title-spacer] + &': { display: 'none' },
                    }}
                />
                <SliceDetailsButton
                    plane={plane}
                    renderer={renderer}
                    frameSource={frame.source}
                    boundary={titleBar}
                />
                <FrameAngleReadout
                    source={frame.source}
                    onReset={frame.onReset}
                    axis={FRAME_AXIS_OF_PLANE[plane]}
                />
            </TileTitleBar>

            <Box
                ref={viewportRef}
                data-viewport-container
                sx={{
                    position: 'relative',
                    flex: 1,
                    minHeight: 0,
                    [TILE_CORNER_HEIGHT_VAR]: `${cornerHeightPx}px`,
                }}
                // The label and the host overlays lie within the view, so
                // the pointer crossing them keeps the readouts and the
                // pick, and a press on them holds the name back as a press
                // on the canvas does; leaving the view drops them.
                onPointerMove={followPointer}
                onPointerDown={holdPointerPress}
                onPointerUp={hoveredRoi.onPointerUp}
                onPointerLeave={clearPointerReadouts}
            >
                <canvas
                    ref={canvasRef}
                    data-testid="tile-canvas"
                    onClick={(event) => selectRoiAt(event, false)}
                    onDoubleClick={(event) => {
                        if (onSelectRoi) {
                            selectRoiAt(event, true);
                        }
                    }}
                    onPointerLeave={hoveredRoi.cancelPendingLookup}
                    // The wheel scrolls or zooms the view under the pick.
                    onWheel={dropHoveredPick}
                    style={{
                        width: '100%',
                        height: '100%',
                        display: 'block',
                        touchAction: 'none',
                    }}
                />
                {/* The name of the contour under the pointer, pinned where
                    the pick resolved, with the deletions of the structure
                    being edited behind a button. */}
                {pick ? (
                    <HoveredStructureLabel
                        containerRef={hoveredRoi.labelRef}
                        name={pick.name}
                        clientX={pick.clientX}
                        clientY={pick.clientY}
                        frameRect={viewportRef.current?.getBoundingClientRect()}
                        offerActions={
                            structureActions?.offersFor(pick.roiId) ?? false
                        }
                        onOpenMenu={(clientX, clientY) =>
                            structureActions?.open(renderer, pick, {
                                clientX,
                                clientY,
                            })
                        }
                    />
                ) : null}
                {/* The engine's draw tools paint cursor feedback (rubber-bands,
                    deform / box-zoom cursors) onto this 2D overlay, layered over
                    the WebGL canvas. pointer-events:none so it never eats tool
                    input — gestures flow to the engine's attached input surface.
                    z-index:auto + this DOM position keeps it above the WebGL
                    canvas yet below the crosshair / POI / HUD siblings. The
                    engine's tools size its backing store on demand. */}
                <canvas
                    ref={overlayCanvasRef}
                    data-testid="tile-overlay-canvas"
                    aria-hidden
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        display: 'block',
                        pointerEvents: 'none',
                    }}
                />
                {overlay}
                <EdgeChips renderer={renderer} plane={plane} />
                <TileCorner
                    scaleBar={scaleBarVisible ? scaleBar : undefined}
                    histograms={windowHistograms}
                    stackRef={cornerRef}
                />
                {/* The value under the pointer with the point it was read
                    at, and between them the dose the tile draws there and
                    the density in force there; with no pointer on the
                    image, where the tile's plane lies, under the dose the
                    tile draws at the pointer. */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 6,
                        right: 6,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 0.5,
                    }}
                >
                    {pointerReadout?.value ? (
                        <>
                            <Box
                                data-testid="cursor-value"
                                sx={{ ...READOUT_BOX_SX, color: '#fff' }}
                            >
                                {formatCursorValue(pointerReadout)}
                            </Box>
                            {pointerDoseLine}
                            {pointerDensity ? (
                                <Box
                                    data-testid={
                                        pointerDensity.namesStructure
                                            ? 'density-override-row'
                                            : 'sct-density-row'
                                    }
                                    sx={{
                                        ...READOUT_BOX_SX,
                                        display: 'flex',
                                        gap: 0.5,
                                        color: '#fff',
                                    }}
                                >
                                    <Box
                                        component="span"
                                        data-testid="density-source"
                                        sx={{
                                            color: READOUT,
                                            ...(pointerDensity.namesStructure && {
                                                maxWidth: 96,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }),
                                        }}
                                    >
                                        {pointerDensity.source}
                                    </Box>
                                    {pointerDensity.quantities.map(
                                        (quantity) => (
                                            <Box
                                                component="span"
                                                key={quantity}
                                            >
                                                {quantity}
                                            </Box>
                                        )
                                    )}
                                </Box>
                            ) : null}
                            <Box
                                data-testid="pointer-coords"
                                sx={READOUT_BOX_SX}
                            >
                                {formatPatientCoordsLine(
                                    pointerReadout.patientMm
                                )}
                            </Box>
                        </>
                    ) : (
                        <>
                            {pointerDoseLine}
                            <PlanePositionReadout renderer={renderer} />
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export default Tile;
